import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClientSchema, type ClientDto, type EmployeeDto } from '@taqon/contracts';
import { useNavigate, useSearchParams } from 'react-router';
import { getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { ImportButton } from '../../shared/import/ImportButton';
import { exportHref } from '../../shared/export/export-href';
import { SearchFilterBar } from '../../shared/ui/filters';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { RecordLink } from '../finance/detail';
import {
  ActiveEmployeePicker,
  CLIENTS,
  EmployeeName,
  isMasked,
  listLabel,
  ListSelect,
  Pager,
  RELEASE_ONE_STAGES,
  STAGE_LABELS,
  useCrmLists,
} from './common';

const PAGE_SIZE = 50;

/**
 * The client list (FRD-CRM-006, CRM-052): operational client reporting by status, lifecycle stage,
 * client type, industry, account owner and source, with a search over the Client ID and company
 * name — all filtered and paged on the server, and kept in the URL so a refresh keeps them. Where
 * the session may, a new client is created here; the Client ID is generated by the system.
 */
export function ClientsPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'crm.client:create');
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const lists = useCrmLists();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [owner, setOwner] = useState<EmployeeDto | null>(null);
  const [search, setSearch] = useState(params.get('search') ?? '');

  const filters = {
    status: params.get('status') ?? 'active',
    stage: params.get('stage') ?? '',
    type: params.get('type') ?? '',
    industry: params.get('industry') ?? '',
    source: params.get('source') ?? '',
    owner: params.get('owner') ?? '',
    search: params.get('search') ?? '',
  };
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const set = (key: keyof typeof filters | 'page', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '' && key !== 'status') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort: 'code',
  });
  const apiFilters: readonly [string, string][] = [
    ['status', filters.status === 'all' ? '' : filters.status],
    ['lifecycle_stage', filters.stage],
    ['client_type', filters.type],
    ['industry', filters.industry],
    ['source', filters.source],
    ['account_owner_id', filters.owner],
    ['search', filters.search],
  ];
  for (const [key, value] of apiFilters) if (value !== '') query.set(`filter[${key}]`, value);
  const clients = useQuery({
    queryKey: ['crm', 'clients', 'list', query.toString()],
    queryFn: () => getPage(`${CLIENTS}?${query.toString()}`, ClientSchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Clients"
        description="The client master every module refers to."
        actions={
          <>
            <ExportButton recordType="crm.client" href={exportHref(CLIENTS, query)} />
            <ImportButton recordType="crm.client" />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddBusinessIcon />}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setCreating(true);
                }}
              >
                New client
              </Button>
            )}
          </>
        }
      />
      <SearchFilterBar
        aria-label="Client filters"
        label="Search by ID or name"
        value={search}
        onChange={setSearch}
        onBlur={() => {
          if (search.trim() !== filters.search) set('search', search.trim());
        }}
        onSubmit={() => {
          set('search', search.trim());
        }}
      >
        <TextField
          select
          size="small"
          label="Status"
          // "All" is the empty value; without these the select would show it blank.
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          value={filters.status === 'all' ? '' : filters.status}
          onChange={(e) => {
            set('status', e.target.value === '' ? 'all' : e.target.value);
          }}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
          <MenuItem value="">All</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Lifecycle stage"
          value={filters.stage}
          onChange={(e) => {
            set('stage', e.target.value);
          }}
        >
          <MenuItem value="">All stages</MenuItem>
          {RELEASE_ONE_STAGES.map((stage) => (
            <MenuItem key={stage} value={stage}>
              {STAGE_LABELS[stage]}
            </MenuItem>
          ))}
        </TextField>
        {(
          [
            ['type', 'Client type', lists.data?.clientType],
            ['industry', 'Industry', lists.data?.industry],
            ['source', 'Source', lists.data?.clientSource],
          ] as const
        ).map(([key, label, values]) => (
          <TextField
            key={key}
            select
            size="small"
            label={label}
            value={filters[key]}
            onChange={(e) => {
              set(key, e.target.value);
            }}
          >
            <MenuItem value="">All</MenuItem>
            {(values ?? []).map((value) => (
              <MenuItem key={value.valueCode} value={value.valueCode}>
                {value.label}
                {value.active ? '' : ' (inactive)'}
              </MenuItem>
            ))}
          </TextField>
        ))}
        {canSeeEmployees &&
          (filters.owner !== '' && owner === null ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2">
                Account owner: <EmployeeName id={filters.owner} />
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  set('owner', '');
                }}
              >
                Clear
              </Button>
            </Stack>
          ) : (
            <ActiveEmployeePicker
              label="Account owner"
              value={owner}
              onChange={(value) => {
                setOwner(value);
                set('owner', value?.id ?? '');
              }}
            />
          ))}
      </SearchFilterBar>
      {clients.isError && <ErrorState message={clients.error.message} />}
      <Section>
        {clients.isPending && <LoadingState label="Loading clients" />}
        {clients.data?.items.length === 0 && <EmptyState title="No clients found" />}
        {clients.data !== undefined && clients.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Clients">
              <TableHead>
                <TableRow>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Stage</TableCell>
                  <TableCell>Client type</TableCell>
                  <TableCell>Industry</TableCell>
                  <TableCell>Account owner</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.data.items.map((client) => (
                  <TableRow key={client.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/crm/clients/${client.id}`}>{client.code}</RecordLink>
                    </TableCell>
                    <TableCell>{client.companyName}</TableCell>
                    <TableCell>{STAGE_LABELS[client.lifecycleStage]}</TableCell>
                    <TableCell>
                      {listLabel(lists.data?.clientType, client.clientTypeCode)}
                    </TableCell>
                    <TableCell>{listLabel(lists.data?.industry, client.industryCode)}</TableCell>
                    <TableCell>
                      <EmployeeName id={client.accountOwnerEmployeeId} />
                    </TableCell>
                    <TableCell>
                      <StatusChip status={client.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={clients.data.total}
              noun="client(s)"
              onPage={(next) => {
                set('page', String(next));
              }}
            />
          </>
        )}
      </Section>
      {creating && (
        <ClientDialog
          onClose={(saved) => {
            setCreating(false);
            if (saved !== undefined) void navigate(`/crm/clients/${saved.id}?created=1`);
          }}
        />
      )}
    </Stack>
  );
}

interface FormValues {
  companyName: string;
  lifecycleStage: string;
  industryCode: string;
  clientTypeCode: string;
  sourceCode: string;
  website: string;
  email: string;
  phone: string;
  trn: string;
  notes: string;
}

const OPTIONAL: ReadonlySet<keyof FormValues> = new Set([
  'industryCode',
  'clientTypeCode',
  'sourceCode',
  'website',
  'email',
  'phone',
  'trn',
  'notes',
]);

function valuesOf(client: ClientDto | undefined): FormValues {
  return {
    companyName: client?.companyName ?? '',
    lifecycleStage: client?.lifecycleStage ?? '',
    industryCode: client?.industryCode ?? '',
    clientTypeCode: client?.clientTypeCode ?? '',
    sourceCode: client?.sourceCode ?? '',
    website: client?.website ?? '',
    email: client?.email ?? '',
    phone: client?.phone ?? '',
    // A masked TRN is not a value: the form starts empty and sends nothing unless replaced.
    trn: client?.trn === undefined || client.trn === null || isMasked(client.trn) ? '' : client.trn,
    notes: client?.notes ?? '',
  };
}

/**
 * Create (no `client`) or edit one client. An edit sends only what changed, with If-Match; an
 * emptied optional field is sent as null ("clear it"). The lifecycle stage and the status have
 * their own actions on the client page, each audited as a stage or status change.
 */
export function ClientDialog({
  client,
  onClose,
}: {
  client?: ClientDto;
  onClose: (saved?: ClientDto) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const lists = useCrmLists();
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const creating = client === undefined;
  const original = valuesOf(client);
  const [values, setValues] = useState<FormValues>(original);
  const [owner, setOwner] = useState<EmployeeDto | null>(null);
  const set = (field: keyof FormValues) => (value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };
  const trnMasked = isMasked(client?.trn ?? null);

  const body: Record<string, string | null> = {};
  for (const field of Object.keys(values) as (keyof FormValues)[]) {
    const value = values[field].trim();
    if (creating) {
      if (value !== '') body[field] = value;
    } else if (field !== 'lifecycleStage' && value !== original[field]) {
      body[field] = OPTIONAL.has(field) && value === '' ? null : value;
    }
  }
  // A masked TRN left untouched is never sent back as "cleared".
  if (!creating && trnMasked && values.trn.trim() === '') delete body.trn;
  if (owner !== null && owner.id !== client?.accountOwnerEmployeeId) {
    body.accountOwnerEmployeeId = owner.id;
  }
  const complete =
    values.companyName.trim() !== '' &&
    (!creating || (values.lifecycleStage !== '' && owner !== null));

  const save = useMutation({
    mutationFn: () =>
      creating
        ? postJson(CLIENTS, body, ClientSchema, session.data?.csrfToken ?? '')
        : patchJson(
            `${CLIENTS}/${client.id}`,
            body,
            ClientSchema,
            session.data?.csrfToken ?? '',
            client.version,
          ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['crm', 'clients'] });
      await queryClient.invalidateQueries({ queryKey: ['crm', 'client'] });
      onClose(saved);
    },
  });

  const text = (field: keyof FormValues, label: string, type = 'text') => (
    <TextField
      label={label}
      type={type}
      value={values[field]}
      onChange={(e) => {
        set(field)(e.target.value);
      }}
    />
  );

  return (
    <FormDialog
      open
      title={creating ? 'New client' : `Edit client ${client.code}`}
      submitLabel={creating ? 'Create client' : 'Save changes'}
      canSubmit={complete && Object.keys(body).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {creating ? (
        <Alert severity="info" variant="outlined">
          The Client ID is generated by the system when the client is saved.
        </Alert>
      ) : (
        <TextField label="Client ID" value={client.code} disabled />
      )}
      <TextField
        label="Company name"
        required
        autoFocus={creating}
        value={values.companyName}
        onChange={(e) => {
          set('companyName')(e.target.value);
        }}
      />
      {creating && (
        <TextField
          select
          label="Lifecycle stage"
          required
          value={values.lifecycleStage}
          onChange={(e) => {
            set('lifecycleStage')(e.target.value);
          }}
        >
          {RELEASE_ONE_STAGES.map((stage) => (
            <MenuItem key={stage} value={stage}>
              {STAGE_LABELS[stage]}
            </MenuItem>
          ))}
        </TextField>
      )}
      {canSeeEmployees ? (
        <ActiveEmployeePicker
          label={creating ? 'Account owner' : 'Change account owner'}
          required={creating}
          value={owner}
          onChange={setOwner}
          helperText={creating ? 'The employee responsible for the relationship.' : 'Optional'}
        />
      ) : (
        <Alert severity="warning" variant="outlined">
          Choosing the account owner needs permission to view employees.
        </Alert>
      )}
      <ListSelect
        label="Industry"
        values={lists.data?.industry}
        value={values.industryCode}
        current={client?.industryCode}
        onChange={set('industryCode')}
      />
      <ListSelect
        label="Client type"
        values={lists.data?.clientType}
        value={values.clientTypeCode}
        current={client?.clientTypeCode}
        onChange={set('clientTypeCode')}
      />
      <ListSelect
        label="Source"
        values={lists.data?.clientSource}
        value={values.sourceCode}
        current={client?.sourceCode}
        onChange={set('sourceCode')}
      />
      {text('website', 'Website')}
      {text('email', 'Email', 'email')}
      {text('phone', 'Phone')}
      <TextField
        label="TRN"
        value={values.trn}
        placeholder={trnMasked ? '•••• masked' : undefined}
        helperText={trnMasked ? 'Masked. Enter a value only to replace it.' : undefined}
        slotProps={{ inputLabel: trnMasked ? { shrink: true } : {} }}
        onChange={(e) => {
          set('trn')(e.target.value);
        }}
      />
      <TextField
        label="Notes"
        multiline
        minRows={2}
        value={values.notes}
        onChange={(e) => {
          set('notes')(e.target.value);
        }}
      />
    </FormDialog>
  );
}
